import { isObject } from 'lodash-es'
import {
  buildFilterPayload,
  excludeFieldFromFilterPayload,
  resolveQueryFiltersParam,
} from '../filter/filter-utils'
import {
  parsePageResult,
  resolveHeadIdField,
  resolveHeadIdValue,
  resolveModuleUrls,
  type BaseControllerPageResult,
} from '../api/api-convention'
import { getGridOptions } from '../methods/query'
import { appendExtraQueryParams } from './append-extra-query-params'
import { resolveGridRequest } from './resolve-grid-request'
import type { DataGrid, FilterPayloadNode, GridColumnInfo } from '../types'

/** 列菜单「列数据」最多展示条数 */
export const COLUMN_MENU_VALUE_LIST_LIMIT = 10

/** distinct 结果缓存 TTL（毫秒） */
export const DISTINCT_COLUMN_CACHE_TTL_MS = 60_000

const DISTINCT_QUERY_ONLY_KEYS = ['field', 'keyword', 'limit']

export interface GridDistinctColumnOption {
  value: unknown
  label: string
}

interface DistinctCacheEntry {
  data: GridDistinctColumnOption[]
  ts: number
}

const distinctCache = new Map<string, DistinctCacheEntry>()
const pendingDistinctRequests = new Map<string, Promise<GridDistinctColumnOption[]>>()

function buildDistinctCacheKey(
  module: string | undefined,
  field: string,
  keyword: string | undefined,
  filtersParam: string | undefined
) {
  return `${module ?? ''}|${field}|${keyword?.trim() ?? ''}|${filtersParam ?? ''}`
}

export function clearDistinctColumnValuesCache() {
  distinctCache.clear()
  pendingDistinctRequests.clear()
}

function readDistinctCache(cacheKey: string): GridDistinctColumnOption[] | null {
  const entry = distinctCache.get(cacheKey)
  if (!entry) {
    return null
  }
  if (Date.now() - entry.ts > DISTINCT_COLUMN_CACHE_TTL_MS) {
    distinctCache.delete(cacheKey)
    return null
  }
  return entry.data.map((item) => ({ ...item }))
}

function writeDistinctCache(cacheKey: string, data: GridDistinctColumnOption[]) {
  distinctCache.set(cacheKey, {
    data: data.map((item) => ({ ...item })),
    ts: Date.now(),
  })
}

/** 将接口返回的多种结构统一为 { value, label } */
export function normalizeDistinctList(
  list: unknown[],
  field: string
): GridDistinctColumnOption[] {
  const options: GridDistinctColumnOption[] = []
  for (const item of list) {
    const normalized = normalizeDistinctItem(item, field)
    if (normalized) {
      options.push(normalized)
    }
  }
  return options
}

function normalizeDistinctItem(
  item: unknown,
  field: string
): GridDistinctColumnOption | null {
  if (item == null) {
    return null
  }
  if (
    typeof item === 'string' ||
    typeof item === 'number' ||
    typeof item === 'boolean'
  ) {
    return { value: item, label: String(item) }
  }
  if (typeof item === 'object') {
    const record = item as Record<string, unknown>
    if (record.value !== undefined && record.value !== null && record.value !== '') {
      const value = record.value
      return {
        value,
        label:
          record.label != null && record.label !== ''
            ? String(record.label)
            : String(value),
      }
    }
    if (field && field in record) {
      const value = record[field]
      if (value === null || value === undefined || value === '') {
        return null
      }
      return { value, label: String(value) }
    }
  }
  return null
}

export async function fetchDistinctColumnValues(
  $grid: DataGrid,
  column: GridColumnInfo,
  keyword?: string
): Promise<GridDistinctColumnOption[]> {
  const options = getGridOptions($grid)
  const { reactData } = $grid
  const urls = resolveModuleUrls(options.module)
  const url = options.distinctColumnUrl || urls.queryDistinctColumnValues
  const field = column.field
  if (!field) {
    return []
  }

  const userFilters = excludeFieldFromFilterPayload(
    $grid.getFilters?.() ?? buildFilterPayload(reactData.filterGroups),
    field
  )

  const params: Record<string, unknown> = {
    field,
    limit: COLUMN_MENU_VALUE_LIST_LIMIT,
  }
  appendExtraQueryParams(params, options, DISTINCT_QUERY_ONLY_KEYS)

  const systemFilters: FilterPayloadNode[] = []
  if (isObject(options.parent)) {
    const headId = resolveHeadIdField(options)
    if (headId) {
      systemFilters.push({
        field: headId,
        operator: '=',
        logic: 'and',
        value: resolveHeadIdValue(options),
      })
    }
  }
  if (keyword) {
    params.keyword = keyword
  }

  options.beforeQuery?.(params as any)

  const filtersParam = resolveQueryFiltersParam(userFilters, systemFilters)
  if (filtersParam) {
    params.filters = filtersParam
  } else {
    delete params.filters
  }

  const cacheKey = buildDistinctCacheKey(
    options.module,
    field,
    keyword,
    typeof params.filters === 'string' ? params.filters : filtersParam
  )

  const cached = readDistinctCache(cacheKey)
  if (cached) {
    return cached
  }

  const pending = pendingDistinctRequests.get(cacheKey)
  if (pending) {
    return pending
  }

  const request = resolveGridRequest($grid)
    .get<BaseControllerPageResult<GridDistinctColumnOption[]>>({ url, params })
    .then((res) => {
      const { list } = parsePageResult<unknown>(res)
      const data = normalizeDistinctList(
        Array.isArray(list) ? list : [],
        field
      )
      writeDistinctCache(cacheKey, data)
      return data
    })
    .finally(() => {
      pendingDistinctRequests.delete(cacheKey)
    })

  pendingDistinctRequests.set(cacheKey, request)
  return request
}
